import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { POS_NAV_GROUPS, posSectionHref, usePosNavStore } from '@/stores/pos-nav-store';

/**
 * The sidebar's contents while the user is inside POS Management: a way back
 * to the main menu, then the POS sections grouped (Catalog, Orders & stock,
 * Customers, Setup). Replaces the top tab strip, which hid its overflow on
 * anything narrower than a wide desktop.
 *
 * Items are anchors to `#/pos-management?tab=<key>`, so they work from the
 * tabbed page and from its sub-routes (product create, PO edit …) alike, and
 * the hash change closes the mobile drawer the way every other nav link does.
 */
export function PosSidebarNav({ collapsed }: { collapsed: boolean }) {
  const items = usePosNavStore((s) => s.items);
  const active = usePosNavStore((s) => s.active);

  const grouped = POS_NAV_GROUPS.map((g) => ({
    label: g.label,
    items: g.keys.map((k) => items.find((i) => i.key === k)).filter((i): i is NonNullable<typeof i> => !!i),
  }));
  const listed = new Set(POS_NAV_GROUPS.flatMap((g) => g.keys));
  const unlisted = items.filter((i) => !listed.has(i.key));
  if (unlisted.length > 0) grouped[grouped.length - 1].items.push(...unlisted);
  const groups = grouped.filter((g) => g.items.length > 0);

  const itemClass = (isActive: boolean) =>
    cn(
      'flex items-center rounded-lg py-2 text-sm font-medium transition-colors',
      collapsed ? 'justify-center px-2' : 'gap-3 px-3',
      isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
    );

  return (
    <nav className={cn('flex-1 overflow-y-auto py-4', collapsed ? 'px-2' : 'px-3')} aria-label="POS Management">
      <a href="#/" title={collapsed ? 'Back to Main Menu' : undefined} className={itemClass(false)}>
        <ArrowLeft className="h-5 w-5 shrink-0" />
        {!collapsed && <span>Back to Main Menu</span>}
      </a>
      {!collapsed && (
        <div className="px-3 pb-1 pt-5 text-[11px] font-semibold uppercase tracking-wide text-foreground">
          POS Management
        </div>
      )}
      {groups.map((g, gi) => (
        <div key={g.label} className={cn(collapsed ? 'mt-3 border-t border-border pt-3' : gi === 0 ? 'mt-1' : 'mt-4')}>
          {!collapsed && (
            <div className="px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/80">
              {g.label}
            </div>
          )}
          <ul className="space-y-1">
            {g.items.map((item) => {
              const Icon = item.icon;
              const isActive = item.key === active;
              return (
                <li key={item.key}>
                  <a
                    href={posSectionHref(item.key)}
                    title={collapsed ? item.label : undefined}
                    aria-current={isActive ? 'page' : undefined}
                    className={itemClass(isActive)}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}
